import { put, call, select, takeLatest } from "redux-saga/effects";
import axios from 'axios';

import API_ENDPOINTS from "@services/apiConfig";
import { nodes } from "@constants/filters";
import { filterActionTypes, filterSelectors, otpActions } from "@store/ducks";
import { getInvoiceStatus } from "@constants/common";
import { getFileNameFromContentDispositionHeader } from "@utils/common";

/********** ACTION_TYPES **********/
export const actionTypes = {
  fetchData: "invoices/INITIATE_FETCH_DATA",
  fetchDataFailed: "invoices/FETCH_DATA_FAILED",
  fetchDataFulfilled: "invoices/FETCH_DATA_FULFILLED",

  downloadInvoice: "invoices/INITIATE_DOWNLOAD_INVOICE",
  downloadInvoiceFailed: "invoices/DOWNLOAD_INVOICE_FAILED",
  downloadInvoiceSuccess: "invoices/DOWNLOAD_INVOICE_SUCCESS",

  downloadAllInvoices: "invoices/INITIATE_DOWNLOAD_ALL_INVOICES",
  downloadAllInvoicesFailed: "invoices/DOWNLOAD_INVOICES_ALL_FAILED",
  downloadAllInvoicesSuccess: "invoices/DOWNLOAD_INVOICES_ALL_SUCCESS",

  uploadInvoice: "invoices/UPLOAD_INVOICE_INITIATED",
  uploadInvoiceFailed: "invoices/UPLOAD_INVOICE_FAILED",
  uploadInvoiceSuccess: "invoices/UPLOAD_INVOICE_SUCCESS",
  clearUpload: "invoices/CLEAR_UPLOAD",
};

/********** REDUCER **********/
const initialState = {
  fetching: false,
  fetched: false,
  data: {},
  errMsg: null,
  download:{
    fetching: false,
    fetched: false,
    data: {},
    errMsg: null,
  },
  downloadAll: {
    fetching: false,
    fetched: false,
    data: {},
    errMsg: null
  },
  upload: {
    fetching: false,
    fetched: false,
    errMsg: null,
    successMsg: null,
  }
};

export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.fetchData: {
      return { ...state, fetching: true };
    }
    case actionTypes.fetchDataFailed: {
      return {
        ...state,
        fetching: false,
        fetched: false,
        errMsg: action.payload,
      };
    }
    case actionTypes.fetchDataFulfilled: {
      return {
        ...state,
        fetching: false,
        fetched: true,
        data: action.payload,
        errMsg: null,
      };
    }
    case actionTypes.downloadInvoice: {
      return { 
        ...state,
         download :
           {
             ...state.download,
            fetching: true
          } 
      };
    }
    case actionTypes.downloadInvoiceFailed: {
      return {
        ...state,
        download:
        {
          ...state.download,
          fetching: false,
          fetched: false,
          errMsg: action.payload,
        }
      };
    }
    case actionTypes.downloadInvoiceSuccess: {
      return {
        ...state,
        download:
        {
          ...state.download,
          fetching: false,
          fetched: true,
          data: action.payload,
          errMsg: null,
        }
      };
    }
    case actionTypes.downloadAllInvoices: {
      return { 
        ...state,
         downloadAll :
           {
             ...state.downloadAll,
            fetching: true
          } 
      };
    }
    case actionTypes.downloadAllInvoicesFailed: {
      return {
        ...state,
        downloadAll:
        {
          ...state.downloadAll,
          fetching: false,
          fetched: false,
          errMsg: action.payload,
        }
      };
    }
    case actionTypes.downloadAllInvoicesSuccess: {
      return {
        ...state,
        downloadAll:
        {
          ...state.downloadAll,
          fetching: false,
          fetched: true,
          data: action.payload,
          errMsg: null,
        }
      };
    }
    case actionTypes.uploadInvoice: {
      return { 
        ...state,
         upload :
           {
             ...state.upload,
            fetching: true
          } 
      };
    }
    case actionTypes.uploadInvoiceFailed: {
      return {
        ...state,
        upload:
        {
          ...state.upload,
          fetching: false,
          fetched: false,
          errMsg: action.payload,
        }
      };
    }
    case actionTypes.uploadInvoiceSuccess: {
      return {
        ...state,
        upload:
        {
          ...state.upload,
          fetching: false,
          fetched: true,
          successMsg: action.payload,
          errMsg: null,
        }
      };
    }
    case actionTypes.clearUpload: {
      return {
        ...state,
        upload:
        {
          ...initialState.upload,
        }
      };
    }
    default: {
      return state;
    }
  }
};

/********** SELECTORS **********/
export const selectors = {
  getRows: (state) =>
    (state.partnerApp.invoices.data && state.partnerApp.invoices.data.invoices) || [],
  getTotalRows: (state) => state.partnerApp.invoices.data && state.partnerApp.invoices.data.total_count,
  isFetched: state => state.partnerApp.invoices.fetched,
  isInvoiceUploadSuccess: (state) => state.partnerApp.invoices.upload.successMsg,
};

/********** ACTION_CREATORS **********/
export const actions = {
  fetchData: (data) => ({
    type: actionTypes.fetchData,
    payload: data,
  }),
  fetchDataFailed: (data) => ({
    type: actionTypes.fetchDataFailed,
    payload: data,
  }),
  fetchDataFulfilled: (data) => ({
    type: actionTypes.fetchDataFulfilled,
    payload: data,
  }),
  downloadInvoice: (data) => ({
    type: actionTypes.downloadInvoice,
    payload: data,
  }),
  downloadInvoiceFailed: (data) => ({
    type: actionTypes.downloadInvoiceFailed,
    payload: data,
  }),
  downloadInvoiceSuccess: (data) => ({
    type: actionTypes.downloadInvoiceSuccess,
    payload: data,
  }),
  downloadAllInvoices: (data) => ({
    type: actionTypes.downloadAllInvoices,
    payload: data,
  }),
  downloadAllInvoicesFailed: (data) => ({
    type: actionTypes.downloadAllInvoicesFailed,
    payload: data,
  }),
  downloadAllInvoicesSuccess: (data) => ({
    type: actionTypes.downloadAllInvoicesSuccess,
    payload: data,
  }),
  uploadInvoice: (data) => ({
    type: actionTypes.uploadInvoice,
    payload: data,
  }),
  uploadInvoiceFailed: (data) => ({
    type: actionTypes.uploadInvoiceFailed,
    payload: data,
  }),
  uploadInvoiceSuccess: (data) => ({
    type: actionTypes.uploadInvoiceSuccess,
    payload: data,
  }),
  clearUpload: () => ({
    type: actionTypes.clearUpload,
  })
};

/********** WORKERS **********/
function* fetchDataWorker() {
  const state = yield select();
  const {
    pageOffset,
    pageSize,
    order,
    orderBy,
    status,
    searchTerm,
    searchText,
  } = filterSelectors.getFilters(state, nodes.invoices);
  try {
    const response = yield call(
        [axios, 'get'],
        API_ENDPOINTS.INVOICES.LIST({ 
          orderBy,
          order,
          pageOffset,
          pageSize,
          status,
          searchTerm,
          searchText,
        })
      )
    if (response.status == 200) {
        let data = response.data.result;
        data.invoices.forEach((el) => {
          el.status = getInvoiceStatus(el.status);
          if(el.status == 'Paid') el['statusReason'] = `Paid on ${el.paymentDate}`;
          else if(el.status == 'Approved') el['statusReason'] = `Approved on ${el.approvalDate}`;
          else if(el.status == 'Pending') el['statusReason'] = `Initiated on ${el.invoiceDate}`;
        })
        yield put(actions.fetchDataFulfilled(data));
    }
  } catch (err) {
    const errMsg = (err && err.message) || "Something went wrong";
    yield put(actions.fetchDataFailed(errMsg));
  }
}

function* downloadAllInvoicesWorker() {
  const state = yield select();
  const {
    pageOffset,
    pageSize,
    order,
    orderBy,
    status,
    searchTerm,
    searchText,
  } = filterSelectors.getFilters(state, nodes.invoices);
  try {
    const response = yield axios.post(API_ENDPOINTS.INVOICES.DOWNLOAD_ALL_INVOICES,
       {
        orderBy,
        order,
        pageOffset,
        pageSize,
        status,
        searchTerm,
        searchText,
        },
      {responseType: 'blob'});
    if (response.status == 200) {
      const fileName = getFileNameFromContentDispositionHeader(response.headers["content-disposition"]);
      const blob = new Blob([response.data], {type: 'application/vnd.ms-excel'});
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
      yield put(actions.downloadAllInvoicesSuccess(response.data.result));
    }
  } catch (err) {
    const errMsg = (err && err.message) || "Something went wrong";
    yield put(actions.downloadAllInvoicesFailed(errMsg));
  } 
}

function* downloadInvoiceWorker(action) {
  const { invoiceNumber, download }  = action.payload;
  try {
    const params = {
      disposition: download ? 'attachment': '',
    }
    const response = yield call(
        [axios, 'get'],
        API_ENDPOINTS.INVOICES.DOWNLOAD_INVOICE({
          invoiceNumber,
          params
        })
      )
    if (response.status == 200) {
      const link = document.createElement('a');
      link.href = response.data.path;
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      yield put(actions.downloadInvoiceSuccess(response.data.path));
    }
  } catch (err) {
    const errMsg = (err && err.message) || "Something went wrong";
    yield put(actions.downloadInvoiceFailed(errMsg));
  }
}

function* uploadInvoiceWorker(action) {
  const { invoiceNumber, resellerFile, resellerInvoiceNo }  = action.payload;
  const formData = new FormData();
  if (resellerFile) {
    formData.append('resellerFile', resellerFile);
  }
  formData.append('resellerInvoiceNo', resellerInvoiceNo)
  try {
    const response = yield axios.post(API_ENDPOINTS.INVOICES.UPLOAD_INVOICE(invoiceNumber),
      formData,
      );
    if (response.status == 200) {
      yield put(actions.uploadInvoiceSuccess(response.data.msg));
      yield put(otpActions.sendOtp({ invoiceNumber }))
    }
  } catch (err) {
    const errMsg = (err && err.message) || "Something went wrong";
    yield put(actions.uploadInvoiceFailed(errMsg));
  }
}

/********** SAGAS **********/
export function* saga() {
  yield takeLatest(actionTypes.fetchData, fetchDataWorker);
  yield takeLatest(filterActionTypes.updateInvoicesFilter, fetchDataWorker);
  yield takeLatest(actionTypes.downloadInvoice, downloadInvoiceWorker);
  yield takeLatest(actionTypes.downloadAllInvoices, downloadAllInvoicesWorker);
  yield takeLatest(actionTypes.uploadInvoice, uploadInvoiceWorker);
}